import formatMoney from '@/lib/formatters/money'
import type { Cart } from '@/lib/types/Cart'
import type { Product } from '@/lib/types/Product'
import { shipping } from '@/lib/constants'

export async function loadCollectionProducts(
  handle: string,
  cartItems: Cart['items'],
): Promise<Product[]> {
  return (
    await fetch(`/collections/${handle}?view=json-endpoint`).then((res) =>
      res.json(),
    )
  ).filter((product: Product) => {
    return !cartItems.find(
      (cartItem) => `${cartItem.product_id}` === `${product.id}`,
    )
  })
}

export type UpsellOffer = AdditionalProduct | UpgradeProducts

interface AdditionalProduct {
  type: 'additional'
  title: string
  product: Product
  cta: string
}

interface UpgradeProducts {
  type: 'upgrade'
  title: string
  productSelections: {
    key: string
    product: Product
    quantity: number
    selectedVariantId: string
    selectedSellingPlanId: string
  }[]
}

export function getUpsellStatus(cart: Cart) {
  const freeShippingReached =
    cart.total_weight >= shipping.free_threshold_grams ||
    cart.total_price >= shipping.free_threshold_grams

  const freeShippingDiff = !freeShippingReached
    ? shipping.free_threshold_grams - cart.total_price
    : 0

  const nSubscriptions = cart.items.filter(
    (item) => !!item.selling_plan_allocation,
  ).length

  return { freeShippingReached, freeShippingDiff, nSubscriptions }
}

export async function getUpsellOffer(cart: Cart): Promise<UpsellOffer | null> {
  const fullProductData = cart.fullProductData
  if (!fullProductData) return null

  const nCartSubscriptions = cart.items.filter(
    (cartItem) => cartItem.selling_plan_allocation,
  ).length

  const cartHasAvailableSubscriptions = cart.items.some((cartItem) => {
    return (
      fullProductData[cartItem.id].variants[cartItem.variant_id]
        .sellingPlanAllocations.length > 0
    )
  })

  if (nCartSubscriptions < 1) {
    if (cartHasAvailableSubscriptions) {
      const upgradableItems = cart.items
        .filter((cartItem) => !cartItem.selling_plan_allocation)
        .filter((cartItem) => {
          return (
            fullProductData[cartItem.id].variants[cartItem.variant_id]
              .sellingPlanAllocations.length > 0
          )
        })
      const potentialSaving = getPotentialSaving(
        upgradableItems,
        fullProductData,
      )
      if (potentialSaving > 0) {
        return Promise.resolve({
          type: 'upgrade',
          title: `Save ${formatMoney(potentialSaving)} and up to ${formatMoney(potentialSaving * 6)} a year when you subscribe. Change or cancel any time, no lock-in!`,
          productSelections: upgradableItems.map((cartItem) => ({
            key: cartItem.key,
            product: fullProductData[cartItem.id],
            quantity: cartItem.quantity,
            selectedVariantId: cartItem.variant_id.toString(),
            selectedSellingPlanId: findDefaultSellingPlan(
              fullProductData[cartItem.id].defaultFrequency,
              fullProductData[cartItem.id].variants[cartItem.variant_id]
                .sellingPlanAllocations,
            ).id.toString(),
          })),
        })
      } else {
        // no potential savings upgrading to subscription
        return null
      }
    } else {
      // TODO cart contains no subscriptions and nothing we can upgrade to subscription
      return null
    }
  } else {
    // cart contains subscriptions

    const hasFreeShipping =
      cart.total_price >= shipping.free_threshold_pence ||
      cart.total_weight >= shipping.free_threshold_pence
    const freeShippingDiff = shipping.free_threshold_pence - cart.total_price

    if (!hasFreeShipping) {
      // cart does not have free shipping

      const recommendedProducts = await loadCollectionProducts(
        'under-free-shipping-subscription-pop-up',
        cart.items,
      )
      const productForFreeShipping = getProductMoreExpensiveThan(
        recommendedProducts,
        freeShippingDiff,
      )
      if (productForFreeShipping) {
        return {
          type: 'additional',
          title: `You're only ${formatMoney(
            freeShippingDiff,
          )} away from free shipping! Add the product below and get more refills instead of paying delivery fees!`,
          product: productForFreeShipping,
          cta: 'Add for {price} and get free shipping',
        }
      } else {
        return {
          type: 'additional',
          title: 'We think you’ll love this product…',
          product: recommendedProducts[0],
          cta: 'Add and checkout',
        }
      }
    } else {
      // cart has free shipping

      const recommendedProducts = await loadCollectionProducts(
        'subscriber-over-free-shipping-pop-up',
        cart.items,
      )

      if (nCartSubscriptions < 5) {
        return {
          type: 'additional',
          title: 'We think you’ll love this product…',
          product: recommendedProducts[0],
          cta: 'Add and checkout',
        }
      } else {
        // cart already has 5+ subscriptions
        return null
      }
    }
  }
}

const getPotentialSaving = (
  upgradableItems: Cart['items'],
  fullProductData: Exclude<Cart['fullProductData'], undefined>,
) => {
  let totalDiscount = 0
  for (const upgradableItem of upgradableItems) {
    const fullProduct = fullProductData[upgradableItem.id]
    const selectedVariant = fullProduct.variants[upgradableItem.variant_id]
    const fullPrice =
      parseInt(selectedVariant.price) * 100 * upgradableItem.quantity
    const discountPercent =
      selectedVariant.sellingPlanAllocations[0].sellingPlan.price_adjustments[0]
        .value
    const discount = fullPrice * (discountPercent / 100)
    totalDiscount += discount
  }
  return totalDiscount / 100
}

const getProductMoreExpensiveThan = (
  products: Product[],
  threshold: number,
) => {
  return products.find((product) => {
    const price = parseInt(product.variants[product.selectedVariantId].price)
    const discountPercent =
      product.variants[product.selectedVariantId].sellingPlanAllocations &&
      product.variants[product.selectedVariantId].sellingPlanAllocations.length
        ? product.variants[product.selectedVariantId].sellingPlanAllocations[0]
            .sellingPlan.price_adjustments[0].value
        : 0
    const subscribePrice = ((100 - discountPercent) / 100) * price
    return subscribePrice > threshold
  })
}

const findDefaultSellingPlan = (
  recurTagValue: string | null,
  sellingPlanAllocations: Product['variants'][string]['sellingPlanAllocations'],
) => {
  if (!recurTagValue) return sellingPlanAllocations[0].sellingPlan
  const matchingPlan = sellingPlanAllocations.find((spa) =>
    new RegExp(recurTagValue, 'i').test(spa.sellingPlan.name),
  )
  if (matchingPlan) {
    return matchingPlan.sellingPlan
  } else {
    return sellingPlanAllocations[0].sellingPlan
  }
}
